import React, { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, PlayCircle, Loader2, ClipboardCheck } from "lucide-react";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");

export default function SchedulePicker({ tenant, onChange, onScanStarted }) {
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const schedule = tenant.schedule || "off";

  const change = async (value) => {
    setBusy(true);
    try {
      const saved = await authApi.tenants.patch(tenant.id, { schedule: value });
      onChange?.(saved);
      toast.success(value === "off" ? "Scheduled scans turned off" : `Scans scheduled ${value} - next run ${fmt(saved.next_run_at)}`);
    } catch (err) {
      toast.error("Could not update schedule", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await authApi.tenants.runSchedule(tenant.id);
      if (res.already_running) toast.info("A scan is already running");
      else toast.success("Change check started", { description: tenant.cis_schedule !== false ? "License scan first, then the CIS scan. You will get an alert here and in the bell if seats, gaps, policies or the compliance score changed." : "You will get an alert here and in the bell if seats, gaps or policies changed." });
      onScanStarted?.(res.id);
    } catch (err) {
      toast.error("Could not run the scan", { description: errorMessage(err) });
    } finally {
      setRunning(false);
    }
  };

  const toggleCis = async (value) => {
    setBusy(true);
    try {
      const saved = await authApi.tenants.patch(tenant.id, { cis_schedule: value });
      onChange?.(saved);
      toast.success(value ? "Scheduled runs now include a CIS scan" : "CIS scan removed from scheduled runs");
    } catch (err) {
      toast.error("Could not update schedule", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="schedule" data-testid="schedule-picker">
      <label className="ws-select" title={schedule !== "off" && tenant.next_run_at ? `Next run ${fmt(tenant.next_run_at)}` : "Automatic scans"}>
        <CalendarClock size={13} />
        <select value={schedule} onChange={(e) => change(e.target.value)} disabled={busy} data-testid="schedule-select">
          <option value="off">Auto-scan: off</option>
          <option value="daily">Auto-scan: daily</option>
          <option value="weekly">Auto-scan: weekly</option>
        </select>
      </label>
      {schedule !== "off" && tenant.next_run_at && (
        <span className="muted small" data-testid="schedule-next">
          next {fmt(tenant.next_run_at)}
        </span>
      )}
      <label className="ws-check small schedule-cis" title="Also run the CIS Microsoft 365 benchmark on every scheduled run and alert when the compliance score drops" data-testid="schedule-cis-toggle">
        <input type="checkbox" checked={tenant.cis_schedule !== false} disabled={busy} onChange={(e) => toggleCis(e.target.checked)} data-testid="schedule-cis-checkbox" />
        <ClipboardCheck size={12} /> Include CIS scan
      </label>
      <button type="button" className="tb-btn sm ghost" onClick={runNow} disabled={running} title="Scan now and alert if anything changed since the last scan" data-testid="schedule-run-now">
        {running ? <Loader2 size={13} className="spin" /> : <PlayCircle size={13} />} Check for changes
      </button>
    </div>
  );
}
